import { getAuth } from "firebase/auth";

const LEGACY_CACHE_KEYS = [
  "persist:root",
  "persist:auth",
  "persist:internalTool",
  "persist:workspace",
  "persist:competitors",
  "auth_state",
  "app_cache_version",
];

export const waitForUser = async () => {
  const auth = getAuth();
  await auth.authStateReady();
  if (!auth.currentUser) throw new Error("No user found");
  return auth.currentUser;
};

// Resolve the current Firebase ID token. getIdToken() returns the SDK's
// in-memory cached token and refreshes it automatically when it is near
// expiry, so there is no app-level cache to keep in sync. Requiring a
// signed-in user here is what stops a token being returned for a session
// Firebase has already invalidated.
export const getAuthToken = async () => {
  const user = await waitForUser();
  const token = await user.getIdToken();
  if (!token) {
    throw new Error("No token found");
  }
  return token;
};

// Force a genuine token refresh after a rejected 401. getIdToken() without
// `true` returns the cached token when it isn't near expiry, so a retry would
// re-send the same bad token; getIdToken(true) mints a fresh one.
export const refreshAuthToken = async () => {
  const user = await waitForUser();
  const newToken = await user.getIdToken(true);
  if (!newToken) {
    throw new Error("No token found");
  }
  return newToken;
};

// Firebase session storage is independent of these application cache keys.
export const clearAuthCache = () => {
  try {
    for (const key of LEGACY_CACHE_KEYS) localStorage.removeItem(key);
  } catch (error) {
    console.warn("Could not clear legacy admin storage:", error);
  }
};

/**
 * Session-loss recovery: the Firebase session is gone (no signed-in user to
 * mint a token), so any authenticated request would fail. Rather than let the
 * failure surface as a data error on whatever page fired the call (the
 * "No user found" symptom), clear the auth cache and hard-navigate to /login
 * with the inactivity banner — the same destination as the idle-logout path.
 * A hard navigation (not an SPA transition) also re-fetches index.html so the
 * post-logout session comes up on the latest deployed bundle.
 */
export const redirectToSessionExpired = () => {
  clearAuthCache();
  window.location.assign("/login?info=session_expired");
};
